/**
 * Top-bar user chip + premium dropdown.
 *
 * Premium pass treatment:
 *  - Chip: brand-gradient avatar disc + Inter Semibold name + chevron.
 *          Hover lifts the gold border and adds a soft warm shadow.
 *  - Menu: warm ivory bg, gold accent strip at the top, refined
 *          typography hierarchy (display name + email + roles caps).
 *          Menu items use Inter Semibold uppercase caps with a small
 *          inline gold dot indicator; hover sweeps a faint gold wash
 *          across the row.
 *
 * Renders nothing when no user is signed in.
 */

import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import LogoutIcon from '@mui/icons-material/Logout';
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined';
import {
  Box,
  Divider,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useAuth } from '../auth/useAuth';

const INK = '#0f0a07';
const IVORY = '#faf6ee';
const GOLD = '#bb8c3f';
const GOLD_LIGHT = '#e9c46a';
const FONT_INTER = '"Inter", "Avenir", -apple-system, BlinkMacSystemFont, sans-serif';

export function UserMenu() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);

  if (!user) return null;

  const open = Boolean(anchor);
  const seed = user.first_name || user.email || '?';
  const initial = (seed[0] ?? '?').toUpperCase();
  const isAdmin = user.roles.some((r) => r.name === 'admin');

  const onProfile = () => {
    setAnchor(null);
    navigate('/profile');
  };

  const onUserGuide = () => {
    setAnchor(null);
    navigate('/user-guide');
  };
  const onAdmin = () => {
    setAnchor(null);
    navigate('/admin');
  };
  const onLogout = () => {
    setAnchor(null);
    logout();
    navigate('/login', { replace: true });
  };

  return (
    <>
      {/* ─── Chip ─────────────────────────────────────────────── */}
      <Box
        onClick={(e) => setAnchor(e.currentTarget)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.25,
          px: { xs: 1, sm: 1.5 },
          py: 0.65,
          borderRadius: 999,
          bgcolor: 'rgba(255, 250, 235, 0.92)',
          border: '1px solid',
          borderColor: 'rgba(187, 140, 63, 0.32)',
          cursor: 'pointer',
          boxShadow: open
            ? '0 6px 18px rgba(187, 140, 63, 0.22)'
            : '0 1px 2px rgba(15, 10, 7, 0.04)',
          backdropFilter: 'blur(6px)',
          transition: 'background-color 180ms ease, border-color 180ms ease, box-shadow 220ms ease',
          '&:hover': {
            bgcolor: '#fff8e7',
            borderColor: GOLD,
            boxShadow: '0 4px 14px rgba(187, 140, 63, 0.20)',
          },
        }}
      >
        {/* Avatar disc — initial letter on the brand gold gradient.
            Stays as-is for everyone; the admin marker lives inline
            below so it never overlaps the initial. */}
        <Box
          sx={{
            width: 28,
            height: 28,
            borderRadius: '50%',
            background: `linear-gradient(135deg, ${GOLD_LIGHT} 0%, ${GOLD} 55%, #8e6a2a 100%)`,
            color: INK,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontFamily: FONT_INTER,
            fontSize: 12.5,
            fontWeight: 700,
            letterSpacing: '0.04em',
            position: 'relative',
            flexShrink: 0,
            boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.35)',
            '&::before': {
              content: '""',
              position: 'absolute',
              inset: 0,
              borderRadius: '50%',
              background:
                'linear-gradient(180deg, rgba(255,255,255,0.30) 0%, rgba(255,255,255,0) 50%)',
              pointerEvents: 'none',
            },
          }}
        >
          {initial}
        </Box>

        {/* Trigger-chip name — capped width with ellipsis since the
            chip has to stay on one line in the header.  Wrapped in a
            Tooltip so the rep can hover to see the full name when
            it's been truncated.  The dropdown's identity block (below)
            shows the full name on its own. */}
        <Tooltip
          title={
            user.first_name && user.last_name
              ? `${user.first_name} ${user.last_name}`
              : user.first_name || user.email
          }
          arrow
          enterDelay={400}
          placement="bottom"
        >
          <Typography
            sx={{
              fontFamily: FONT_INTER,
              fontSize: 13,
              fontWeight: 600,
              letterSpacing: '0.02em',
              color: INK,
              display: { xs: 'none', sm: 'inline' },
              maxWidth: { sm: 160, md: 220 },
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {user.first_name && user.last_name
              ? `${user.first_name} ${user.last_name}`
              : user.first_name || user.email}
          </Typography>
        </Tooltip>

        {/* Admin marker — a small pulsing gold dot + "ADMIN" gold caps.
            Uses the same eyebrow vocabulary the brand already uses
            elsewhere ("INTERNAL PLATFORM", section eyebrows on cards)
            so it reads as part of the system rather than as a
            bolted-on badge.  The dot does a soft radial-pulse, like
            a candle catching light — discoverable but never demanding
            attention.  Visible only for admins. */}
        {isAdmin && (
          <Box
            aria-label="Admin"
            sx={{
              display: { xs: 'none', sm: 'inline-flex' },
              alignItems: 'center',
              gap: 0.6,
              flexShrink: 0,
              pl: 0.25,
            }}
          >
            <Box
              sx={{
                width: 5,
                height: 5,
                borderRadius: '50%',
                backgroundColor: GOLD,
                '@keyframes adminDotPulse': {
                  '0%, 100%': {
                    boxShadow: '0 0 0 0 rgba(187, 140, 63, 0.7)',
                    transform: 'scale(1)',
                  },
                  '50%': {
                    boxShadow: '0 0 0 6px rgba(187, 140, 63, 0)',
                    transform: 'scale(1.05)',
                  },
                },
                animation: 'adminDotPulse 2.4s ease-in-out infinite',
              }}
            />
            <Typography
              component="span"
              sx={{
                fontFamily: FONT_INTER,
                fontSize: 9.5,
                fontWeight: 700,
                letterSpacing: '0.22em',
                color: GOLD,
                textTransform: 'uppercase',
                whiteSpace: 'nowrap',
              }}
            >
              Admin
            </Typography>
          </Box>
        )}

        <KeyboardArrowDownIcon
          sx={{
            fontSize: 16,
            color: GOLD,
            display: { xs: 'none', sm: 'inline' },
            transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 220ms ease',
          }}
        />
      </Box>

      {/* ─── Menu ─────────────────────────────────────────────── */}
      <Menu
        anchorEl={anchor}
        open={open}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            sx: {
              mt: 1.25,
              width: 320,
              maxWidth: 'calc(100vw - 32px)',
              overflow: 'hidden',
              borderRadius: 1.5,
              bgcolor: IVORY,
              border: '1px solid rgba(187, 140, 63, 0.18)',
              boxShadow: '0 16px 36px rgba(15, 10, 7, 0.14), 0 2px 6px rgba(15, 10, 7, 0.08)',
              position: 'relative',
              // Gold accent strip at the very top — same gradient as
              // the auth-pages seam.
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '2px',
                background: `linear-gradient(to right, ${GOLD} 0%, ${GOLD_LIGHT} 50%, ${GOLD} 100%)`,
              },
            },
          },
        }}
      >
        {/* Identity block */}
        <Box sx={{ px: 2.25, pt: 2.25, pb: 1.75 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
              sx={{
                width: 38,
                height: 38,
                borderRadius: '50%',
                background: `linear-gradient(135deg, ${GOLD_LIGHT} 0%, ${GOLD} 55%, #8e6a2a 100%)`,
                color: INK,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontFamily: FONT_INTER,
                fontSize: 15,
                fontWeight: 700,
                letterSpacing: '0.04em',
                boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.35)',
                flexShrink: 0,
              }}
            >
              {initial}
            </Box>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              {/* Name wraps to multiple lines if long — the dropdown
                  is the canonical place to see your full identity, so
                  no ellipsis here.  word-break: break-word lets a
                  long single token wrap rather than overflow. */}
              <Typography
                sx={{
                  fontFamily: FONT_INTER,
                  fontSize: 14,
                  fontWeight: 600,
                  letterSpacing: '-0.005em',
                  color: INK,
                  lineHeight: 1.3,
                  wordBreak: 'break-word',
                }}
              >
                {user.first_name} {user.last_name}
              </Typography>
              {/* Email — keep single-line + ellipsis. Emails don't
                  break naturally and wrapping mid-address reads
                  worse than a clean truncation. Hover shows full. */}
              <Tooltip title={user.email} arrow enterDelay={400} placement="bottom-start">
                <Typography
                  sx={{
                    fontFamily: FONT_INTER,
                    fontSize: 11.5,
                    color: 'rgba(15, 10, 7, 0.55)',
                    mt: 0.4,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {user.email}
                </Typography>
              </Tooltip>
            </Box>
          </Box>
        </Box>

        <Divider sx={{ borderColor: 'rgba(15, 10, 7, 0.08)' }} />

        {/* Menu items */}
        <PremiumMenuItem
          icon={<AccountCircleIcon fontSize="small" />}
          label="Account Settings"
          onClick={onProfile}
        />
        <PremiumMenuItem
          icon={<MenuBookOutlinedIcon fontSize="small" />}
          label="User Guide"
          onClick={onUserGuide}
        />
        {isAdmin && (
          <PremiumMenuItem
            icon={<AdminPanelSettingsIcon fontSize="small" />}
            label="Admin"
            accent
            onClick={onAdmin}
          />
        )}
        <Divider sx={{ borderColor: 'rgba(15, 10, 7, 0.06)', mx: 1.5 }} />
        <PremiumMenuItem
          icon={<LogoutIcon fontSize="small" />}
          label="Sign out"
          onClick={onLogout}
        />
      </Menu>
    </>
  );
}

interface PremiumMenuItemProps {
  icon: React.ReactNode;
  label: string;
  accent?: boolean;
  onClick: () => void;
}

function PremiumMenuItem({ icon, label, accent, onClick }: PremiumMenuItemProps) {
  return (
    <MenuItem
      onClick={onClick}
      sx={{
        py: 1.25,
        px: 2.25,
        position: 'relative',
        transition: 'background-color 160ms ease',
        '&:hover': {
          bgcolor: 'rgba(187, 140, 63, 0.08)',
        },
        '&::after': accent
          ? {
              content: '""',
              position: 'absolute',
              left: 0,
              top: 8,
              bottom: 8,
              width: 2,
              background: `linear-gradient(to bottom, ${GOLD_LIGHT}, ${GOLD})`,
              borderRadius: 1,
            }
          : undefined,
      }}
    >
      <ListItemIcon
        sx={{
          color: accent ? GOLD : 'rgba(15, 10, 7, 0.55)',
          minWidth: 32,
        }}
      >
        {icon}
      </ListItemIcon>
      <ListItemText
        primaryTypographyProps={{
          sx: {
            fontFamily: FONT_INTER,
            fontSize: 13,
            fontWeight: accent ? 600 : 500,
            color: accent ? INK : 'rgba(15, 10, 7, 0.82)',
            letterSpacing: '0.01em',
          },
        }}
      >
        {label}
      </ListItemText>
    </MenuItem>
  );
}
